import { Button, Card, Icon, Image, Loader, Message, Modal, Table } from 'semantic-ui-react';
import React, { FunctionComponent, useContext, useEffect, useState } from 'react';
import { t } from 'i18next';
import ReactJson from 'react-json-view';
import { ModalProps } from '../../../../types/ModalProps';
import { api } from '../../../../services/fetch/api';
import { AppContext } from '../../../../model/context/app/app';
import { useParams } from 'react-router-dom';
import dateToString from '../../../../services/misc/dateToString';

interface RawMailJetEvent {
    type: string;
    created: string;
    payload: string;
}
interface MailJetEvent {
    event: 'sent' | 'blocked' | 'bounce' | 'open' | 'click' | 'spam' | 'unsub';
    [key: string]: any;
}

type MailJetEventEntry = {
    type: string;
    created: string;
    payload: MailJetEvent;
};

function isErrorEvent(event: string): boolean {
    switch (event) {
        case 'blocked':
        case 'bounce':
            return true;
        default:
            return false;
    }
}

function isWarningWorthy(event: string): boolean {
    switch (event) {
        case 'unsub':
        case 'spam':
            return true;
        default:
            return false;
    }
}

export default function LeadMailJetEventsComponent() {
    const [showModal, setShowModal] = useState(false);

    return (
        <Card fluid>
            <Card.Content>
                <Card.Header>
                    {t('lead.detail.box.mailjetEvents.headline')}
                    <span style={{ padding: '15px' }}>
                        <Button
                            data-testid={'MailJetEventModal.open'}
                            className="ui small basic icon button"
                            title={t('lead.detail.box.mailjetEvents.button')}
                            onClick={() => setShowModal(true)}>
                            <Icon name={'mail'} />
                        </Button>
                    </span>
                </Card.Header>
            </Card.Content>
            <MailJetEventModal showModal={showModal} onClose={() => setShowModal(false)}></MailJetEventModal>
        </Card>
    );
}

export const MailJetEventModal: FunctionComponent<ModalProps> = ({ showModal = false, onClose }) => {
    const [mailJetEntries, setMailJetEntries] = useState<MailJetEventEntry[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const context = useContext(AppContext);
    const { leadId } = useParams();

    const toTableEntry = (body: RawMailJetEvent[]): MailJetEventEntry[] => {
        return body.map((value) => ({
            type: value.type,
            created: value.created,
            payload: JSON.parse(value.payload),
        }));
    };

    async function fetchMailJetEvents() {
        const resp = await fetch(`${api.mailJetEvents}/${leadId}`, {
            method: 'GET',
            headers: {
                'Accept-Language': 'de-DE',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${context.token}`,
            },
        });
        if (resp.ok) {
            setMailJetEntries(toTableEntry(await resp.json()));
        } else {
            setIsError(true);
            console.error({
                status: resp.status,
                text: resp.statusText,
            });
        }
        setIsLoading(false);
    }

    useEffect(() => {
        if (showModal) {
            fetchMailJetEvents();
        }
    }, [showModal]);

    return (
        <Modal
            open={showModal}
            size={'large'}
            closeOnEscape={true}
            closeOnTriggerBlur={true}
            onClose={onClose}
            data-testid={'MailJetEventModal'}>
            <Modal.Header>{t('lead.detail.box.mailjetEvents.headline')}</Modal.Header>
            <Modal.Content scrolling={true}>
                {isLoading && <Loader active={isLoading} content={'Loading'} />}
                {isError && (
                    <Message error data-testid={'MailJetEventModal.error'}>
                        <Message.Header>{t('lead.detail.box.mailjetEvents.errorHeadline')}</Message.Header>
                        <p>{t('lead.detail.box.mailjetEvents.errorHint')}</p>
                    </Message>
                )}
                {!isLoading && !isError && (
                    <Table celled data-testid={'MailJetEventModal.table'}>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>{t('lead.detail.box.mailjetEvents.table.mailType')}</Table.HeaderCell>
                                <Table.HeaderCell>{t('lead.detail.box.mailjetEvents.table.date')}</Table.HeaderCell>
                                <Table.HeaderCell>{t('lead.detail.box.mailjetEvents.table.event')}</Table.HeaderCell>
                                <Table.HeaderCell>{t('lead.detail.box.mailjetEvents.table.payload')}</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>

                        <Table.Body>
                            {mailJetEntries.length == 0 ? (
                                <Table.Row warning>
                                    <Table.Cell
                                        colSpan={4}
                                        textAlign={'center'}
                                        singleLine={true}
                                        content={t('lead.detail.box.mailjetEvents.noDataHint')}
                                    />
                                </Table.Row>
                            ) : (
                                mailJetEntries.map((value, index) => (
                                    <Table.Row
                                        key={index}
                                        error={isErrorEvent(value.payload.event)}
                                        warning={isWarningWorthy(value.payload.event)}>
                                        <Table.Cell singleLine={true} content={value.type} width={1} />
                                        <Table.Cell
                                            singleLine={true}
                                            content={dateToString(value.created, 'dateLong')}
                                            width={1}
                                        />
                                        <Table.Cell singleLine={true} content={value.payload.event} width={1} />
                                        <Table.Cell singleLine={true} collapsing={true}>
                                            <ReactJson
                                                src={value.payload}
                                                name={false}
                                                displayDataTypes={false}
                                                enableClipboard={false}
                                                collapsed={true}
                                            />
                                        </Table.Cell>
                                    </Table.Row>
                                ))
                            )}
                        </Table.Body>
                    </Table>
                )}
            </Modal.Content>
        </Modal>
    );
};
